/** Minimal CSV helpers for job/contact batch intake. */

export function parseCsvLine(line: string): string[] {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === "," && !inQuotes) {
      result.push(current.trim());
      current = "";
    } else {
      current += ch;
    }
  }
  result.push(current.trim());
  return result;
}

export interface ParsedCsvTable {
  /** Empty when the first row was treated as data (URL-only / no headers). */
  headers: string[];
  rows: string[][];
  hasHeader: boolean;
}

function looksLikeUrl(value: string): boolean {
  return /^https?:\/\//i.test(value.trim());
}

function looksLikeHeaderRow(cells: string[]): boolean {
  if (cells.length === 0) return false;
  if (cells.every((c) => looksLikeUrl(c))) return false;
  const joined = cells.join(" ").toLowerCase();
  return /url|link|company|employer|title|role|position|location|notes/.test(joined);
}

/** Parse CSV text into headers + data rows. Detects header vs URL-only lists. */
export function parseCsvTable(text: string): ParsedCsvTable {
  const lines = text
    .replace(/^\uFEFF/, "")
    .split(/\r?\n/)
    .map((l) => l.trimEnd())
    .filter((l) => l.trim().length > 0);

  if (lines.length === 0) {
    return { headers: [], rows: [], hasHeader: false };
  }

  const first = parseCsvLine(lines[0]);

  // Single-column URL list (with or without a "url" header)
  if (first.length === 1) {
    const rest = lines.slice(1).map((l) => parseCsvLine(l));
    if (looksLikeUrl(first[0])) {
      return {
        headers: ["url"],
        rows: [[first[0]], ...rest.map((r) => [r[0] ?? ""])],
        hasHeader: false,
      };
    }
    if (/^url$|^link$|^job_url$/i.test(first[0])) {
      return {
        headers: ["url"],
        rows: rest.map((r) => [r[0] ?? ""]),
        hasHeader: true,
      };
    }
  }

  if (looksLikeHeaderRow(first)) {
    return {
      headers: first.map((h) => h.trim()),
      rows: lines.slice(1).map((l) => parseCsvLine(l)),
      hasHeader: true,
    };
  }

  // Headerless multi-column: synthesize col_0… and treat first column as url when it looks like one
  const headers = first.map((_, i) => `col_${i}`);
  return {
    headers,
    rows: lines.map((l) => parseCsvLine(l)),
    hasHeader: false,
  };
}

/** Newline-separated URLs (ignores blanks and # comments). */
export function parseUrlList(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l && !l.startsWith("#") && looksLikeUrl(l));
}
